class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.reservationStorageKey = 'klaim-cart-reservation-end-at';
    this.reservationTimer = null;
    this.handleCartLinkClick = this.handleCartLinkClick.bind(this);
    this.headerPanels = window.KlaimHeaderPanels;
    this.headerPanels?.register('cart', { element: this, close: () => this.close() });
    this.inert = true;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    document.addEventListener('click', this.handleCartLinkClick);
    this.setHeaderCartIconAccessibility();
    this.syncAnnouncement();
    this.initializeCartDrawerUi();
    this.openFromRedirect();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-link');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.classList.contains('active') ? this.close() : this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.classList.contains('active') ? this.close() : this.open(cartLink);
      }
    });
  }

  handleCartLinkClick(event) {
    if (event.defaultPrevented) return;

    const link = event.target.closest('a[href]');
    if (!link || link.id === 'cart-link') return;

    const linkUrl = new URL(link.href, window.location.origin);
    const cartUrl = new URL(window.routes?.cart_url || '/cart', window.location.origin);
    const normalizePath = (path) => path.replace(/\/+$/, '') || '/';

    if (linkUrl.origin !== window.location.origin || normalizePath(linkUrl.pathname) !== normalizePath(cartUrl.pathname)) return;

    event.preventDefault();
    this.open(link);
  }

  openFromRedirect() {
    try {
      if (window.sessionStorage.getItem('klaim-open-cart-drawer') !== 'true') return;
      window.sessionStorage.removeItem('klaim-open-cart-drawer');
      window.requestAnimationFrame(() => this.open());
    } catch (error) {}
  }

  open(triggeredBy) {
    clearTimeout(this.openTimer);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.headerPanels?.activate('cart', triggeredBy || document.querySelector('#cart-link'));
    this.inert = false;
    if (!this.headerPanels) this.syncAnnouncement();
    this.resetItemsScroll();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this.openTimer = setTimeout(() => {
      this.classList.add('animate', 'active');
      this.resetItemsScroll();
      if (this.headerPanels) {
        const surface = this.querySelector('.drawer__inner');
        surface?.removeAttribute('aria-modal');
        surface?.setAttribute('role', 'region');
        surface?.focus({ preventScroll: true });
      }
    });

    if (!this.headerPanels) this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
      this.resetItemsScroll();
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    this.initializeCartDrawerUi();
  }

  syncAnnouncement() {
    const host = this.querySelector('[data-klaim-cart-announcement]');
    if (!host) return;

    const source = document.querySelector('#HdrAnnouncementBar');
    host.replaceChildren();

    if (!source) {
      host.hidden = true;
      return;
    }

    const clone = source.cloneNode(true);
    clone.removeAttribute('id');
    clone.removeAttribute('hidden');
    clone.removeAttribute('style');
    clone.setAttribute('aria-hidden', 'false');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    host.appendChild(clone);
    host.hidden = false;
  }

  resetItemsScroll() {
    const cartItemsScroller = this.querySelector('#CartDrawer-CartItems');
    if (!cartItemsScroller) return;

    cartItemsScroller.scrollTop = 0;
    window.requestAnimationFrame(() => {
      cartItemsScroller.scrollTop = 0;
    });
  }

  close() {
    clearTimeout(this.openTimer);
    this.classList.remove('active');
    this.inert = true;
    if (!this.headerPanels) removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.headerPanels?.deactivate('cart');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    this.resetItemsScroll();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.initializeCartDrawerUi();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeCartDrawerUi() {
    this.initializeReservationTimer();
  }

  initializeReservationTimer() {
    const timerRoot = this.querySelector('#cart-reservation-timer');
    const countdown = this.querySelector('#cart-timer-countdown');
    const isEmpty = this.classList.contains('is-empty') || !timerRoot || !countdown;

    if (this.reservationTimer) {
      window.clearInterval(this.reservationTimer);
      this.reservationTimer = null;
    }

    if (isEmpty) {
      window.localStorage.removeItem(this.reservationStorageKey);
      return;
    }

    const durationSeconds = Number(timerRoot.dataset.durationSeconds || 600);
    const now = Date.now();
    const storedEndAt = Number(window.localStorage.getItem(this.reservationStorageKey));
    const endAt = storedEndAt && storedEndAt > now ? storedEndAt : now + durationSeconds * 1000;

    window.localStorage.setItem(this.reservationStorageKey, String(endAt));

    const renderCountdown = () => {
      const remainingMs = Math.max(0, endAt - Date.now());
      const totalSeconds = Math.floor(remainingMs / 1000);
      const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
      const seconds = String(totalSeconds % 60).padStart(2, '0');
      countdown.textContent = `${minutes}:${seconds}`;

      if (remainingMs <= 0 && this.reservationTimer) {
        window.clearInterval(this.reservationTimer);
        this.reservationTimer = null;
      }
    };

    renderCountdown();
    this.reservationTimer = window.setInterval(renderCountdown, 1000);
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
